import React, { useState, useEffect } from 'react';
import './Settings.css';
import { validateApiKey, refreshModelsFromAPI, getCachedModels, isCacheValid, getAvailableModels } from '../services/openRouterService';
import SearchableSelect from './SearchableSelect';

const ELEVENLABS_HELP_URL = 'https://help.elevenlabs.io/hc/en-us/articles/14599447207697-How-do-I-authorize-myself-using-an-API-key';
const OPENROUTER_KEYS_URL = 'https://openrouter.ai/keys';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    elevenLabsApiKey: '',
    openRouterApiKey: '',
    defaultTtsService: 'elevenlabs',
    autoSave: true
  });
  
  // WordPress logins state
  const [wordPressLogins, setWordPressLogins] = useState([]);
  const [newWordPressLogin, setNewWordPressLogin] = useState({
    label: '',
    url: '',
    username: '',
    appPassword: ''
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [isRefreshingModels, setIsRefreshingModels] = useState(false);
  const [openRouterTestStatus, setOpenRouterTestStatus] = useState(null); // { message: string, type: 'success' | 'error' | 'loading' }
  const [showTestModal, setShowTestModal] = useState(false);
  const [elevenLabsTestStatus, setElevenLabsTestStatus] = useState(null); // { message: string, type: 'success' | 'error' | 'loading' }
  const [showElevenLabsTestModal, setShowElevenLabsTestModal] = useState(false);
  const [showSettingsSavedModal, setShowSettingsSavedModal] = useState(false);
  const [settingsSavedError, setSettingsSavedError] = useState(null); // error message if save failed
  const [modelsLastUpdated, setModelsLastUpdated] = useState(null);
  const [modelsStats, setModelsStats] = useState({ total: 0, available: 0, unavailable: 0 });
  
  // Default AI Model Selection
  const [defaultModel, setDefaultModel] = useState('');
  const [availableModelsForDefault, setAvailableModelsForDefault] = useState([]);
  const sanitizeApiKey = (value) => (value || '').replace(/[^a-zA-Z0-9_-]/g, '');
  const getElevenLabsHeaders = (apiKey, extra = {}) => ({
    ...extra,
    'xi-api-key': apiKey
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    console.log('🔍 Settings: Loading settings from localStorage...');
    
    const savedSettings = localStorage.getItem('ttsSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        console.log('✅ Settings: Loaded ttsSettings from localStorage');
        setSettings(prev => ({
          ...prev,
          ...parsed,
          elevenLabsApiKey: sanitizeApiKey(parsed.elevenLabsApiKey || prev.elevenLabsApiKey)
        }));
      } catch (error) {
        console.error('❌ Settings: Error loading settings:', error);
      }
    }
    
    // Load OpenRouter API key separately (priority: separate key > ttsSettings)
    const openRouterKey = localStorage.getItem('openrouter_api_key');
    if (openRouterKey) {
      console.log('✅ Settings: Loaded OpenRouter API key from localStorage (separate key)');
      setSettings(prev => ({ ...prev, openRouterApiKey: openRouterKey }));
      // Also ensure it's in ttsSettings for consistency
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          if (parsed.openRouterApiKey !== openRouterKey) {
            parsed.openRouterApiKey = openRouterKey;
            localStorage.setItem('ttsSettings', JSON.stringify(parsed));
            console.log('✅ Settings: Synced OpenRouter API key to ttsSettings');
          }
        } catch (e) {
          console.error('❌ Settings: Error syncing API key to ttsSettings:', e);
        }
      }
    } else {
      console.log('⚠️ Settings: No OpenRouter API key found in localStorage');
    }
    
    // Load WordPress logins
    const savedWordPressLogins = localStorage.getItem('wordPressLogins');
    if (savedWordPressLogins) {
      try {
        const parsed = JSON.parse(savedWordPressLogins);
        setWordPressLogins(parsed);
      } catch (error) {
        console.error('Error loading WordPress logins:', error);
      }
    }
    
    // Load model cache info
    updateModelsInfo();
    
    // Load default model
    loadDefaultModel();
  }, []);
  
  // Update models info from cache
  const updateModelsInfo = () => {
    const cached = getCachedModels();
    if (cached && cached.models) {
      setModelsLastUpdated(cached.timestamp);
      const total = cached.models.length;
      const available = cached.models.filter(m => m.isAvailable !== false).length;
      const unavailable = total - available;
      setModelsStats({ total, available, unavailable });
    } else {
      setModelsLastUpdated(null);
      setModelsStats({ total: 0, available: 0, unavailable: 0 });
    }
  };
  
  // Load default model
  const loadDefaultModel = async () => {
    try {
      const savedDefaultModel = localStorage.getItem('default_ai_model');
      if (savedDefaultModel) {
        setDefaultModel(savedDefaultModel);
      }
      
      // Load available models for dropdown
      const apiKey = localStorage.getItem('openrouter_api_key');
      const models = await getAvailableModels(false, apiKey);
      const modelOptions = models
        .filter(m => m.isAvailable !== false)
        .map(m => ({
          id: m.id,
          value: m.id,
          label: `${m.name}${m.isFree ? ' (free)' : ''}`,
        }));
      setAvailableModelsForDefault(modelOptions);
    } catch (error) {
      console.error('Error loading default model:', error);
    }
  };
  
  // Save default model
  const handleSaveDefaultModel = () => {
    if (defaultModel) {
      localStorage.setItem('default_ai_model', defaultModel);
      setSaveStatus('Default AI model saved successfully!');
    } else {
      localStorage.removeItem('default_ai_model');
      setSaveStatus('Default AI model cleared!');
    }
    setTimeout(() => setSaveStatus(''), 3000);
  };
  
  // Refresh models from API
  const handleRefreshModels = async () => {
    if (!settings.openRouterApiKey) {
      alert('Please enter your OpenRouter API key first.');
      return;
    }
    
    setIsRefreshingModels(true);
    setSaveStatus('Refreshing models from OpenRouter...');
    
    try {
      const models = await refreshModelsFromAPI(settings.openRouterApiKey);
      setSaveStatus(`✅ Models refreshed successfully! Found ${models.length} models.`);
      updateModelsInfo();
      
      // Reload default model options
      await loadDefaultModel();
      
      // Check for new models notification
      const newModelsNotification = localStorage.getItem('openrouter_new_models');
      if (newModelsNotification) {
        try {
          const notification = JSON.parse(newModelsNotification);
          if (notification.count > 0) {
            setTimeout(() => {
              alert(`🎉 ${notification.count} new model(s) available! Check the model dropdowns to see them.`);
            }, 500);
          }
        } catch (e) {
          console.error('Error parsing new models notification:', e);
        }
      }
      
      setTimeout(() => setSaveStatus(''), 5000);
    } catch (error) {
      setSaveStatus('❌ Failed to refresh models: ' + error.message);
      console.error('Error refreshing models:', error);
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setIsRefreshingModels(false);
    }
  };

  // Save settings to localStorage
  const saveSettings = async () => {
    setIsSaving(true);
    setSaveStatus('Saving...');
    setSettingsSavedError(null);
    try {
      const normalizedSettings = {
        ...settings,
        elevenLabsApiKey: sanitizeApiKey(settings.elevenLabsApiKey)
      };
      localStorage.setItem('ttsSettings', JSON.stringify(normalizedSettings));
      // Save OpenRouter API key separately
      if (settings.openRouterApiKey) {
        localStorage.setItem('openrouter_api_key', settings.openRouterApiKey);
      }
      setSaveStatus('Settings saved successfully!');
      setShowSettingsSavedModal(true);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('Error saving settings. Please try again.');
      setSettingsSavedError(error?.message || 'Error saving settings. Please try again.');
      setShowSettingsSavedModal(true);
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Save WordPress logins
  const saveWordPressLogins = () => {
    try {
      localStorage.setItem('wordPressLogins', JSON.stringify(wordPressLogins));
      setSaveStatus('WordPress logins saved successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('Error saving WordPress logins. Please try again.');
      console.error('Error saving WordPress logins:', error);
    }
  };

  // Auto-save WordPress logins when they change
  useEffect(() => {
    if (wordPressLogins.length > 0) {
      localStorage.setItem('wordPressLogins', JSON.stringify(wordPressLogins));
    }
  }, [wordPressLogins]);

  // Add new WordPress login
  const addWordPressLogin = () => {
    if (!newWordPressLogin.label || !newWordPressLogin.url || !newWordPressLogin.username || !newWordPressLogin.appPassword) {
      alert('Please fill in all fields');
      return;
    }
    
    const newLogin = {
      id: Date.now(),
      ...newWordPressLogin
    };
    
    setWordPressLogins(prev => [...prev, newLogin]);
    setNewWordPressLogin({ label: '', url: '', username: '', appPassword: '' });
    saveWordPressLogins();
  };

  // Delete WordPress login
  const deleteWordPressLogin = (id) => {
    if (window.confirm('Are you sure you want to delete this WordPress login?')) {
      setWordPressLogins(prev => prev.filter(login => login.id !== id));
      saveWordPressLogins();
    }
  };

  // Test WordPress connection
  const testWordPressConnection = async (login) => {
    setSaveStatus(`Testing connection to ${login.label}...`);
    try {
      // First, ensure URL has proper format
      let testUrl = login.url;
      if (!testUrl.startsWith('http://') && !testUrl.startsWith('https://')) {
        testUrl = 'https://' + testUrl;
      }
      if (testUrl.endsWith('/')) {
        testUrl = testUrl.slice(0, -1);
      }
      
      console.log('Testing WordPress connection to:', testUrl);
      
      const response = await fetch(`${testUrl}/wp-json/wp/v2/posts`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${btoa(`${login.username}:${login.appPassword}`)}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('WordPress test response status:', response.status);
      
             if (response.ok) {
         const successMessage = `✅ ${login.label}: Connected successfully`;
         console.log(successMessage);
         setSaveStatus(successMessage);
         // Also show an alert for immediate feedback
         alert(successMessage);
       } else {
         const errorText = await response.text();
         console.error('WordPress test error response:', errorText);
         const errorMessage = `❌ ${login.label}: Connection failed (${response.status}) - Check credentials`;
         setSaveStatus(errorMessage);
         alert(errorMessage);
       }
     } catch (error) {
       console.error('WordPress test error:', error);
       const errorMessage = `❌ ${login.label}: Connection failed - ${error.message}`;
       setSaveStatus(errorMessage);
       alert(errorMessage);
     }
     setTimeout(() => setSaveStatus(''), 5000);
  };

  // Save on change if autoSave is enabled
  useEffect(() => {
    if (settings.autoSave) {
      localStorage.setItem('ttsSettings', JSON.stringify(settings));
      // Also save OpenRouter API key separately for other components to access
      if (settings.openRouterApiKey) {
        localStorage.setItem('openrouter_api_key', settings.openRouterApiKey);
      } else {
        // If API key is cleared, also remove it from separate storage
        localStorage.removeItem('openrouter_api_key');
      }
    }
  }, [settings, settings.autoSave, settings.openRouterApiKey]);

  // Test ElevenLabs API key
  const testElevenLabsKey = async () => {
    const apiKey = sanitizeApiKey(settings.elevenLabsApiKey);
    if (!apiKey) {
      alert('Please enter your ElevenLabs API key first.');
      return;
    }
    
    setElevenLabsTestStatus({ message: 'Testing ElevenLabs connection...', type: 'loading' });
    setShowElevenLabsTestModal(true);
    setSaveStatus('');
    
    let testResultType = 'error';
    try {
      const headers = getElevenLabsHeaders(apiKey, { 'Accept': 'application/json' });
      let response = await fetch('https://api.elevenlabs.io/v1/user', { headers });
      
      if (!response.ok) {
        response = await fetch('https://api.elevenlabs.io/v1/voices', { headers });
      }
      
      if (response.ok) {
        const successMessage = '✅ ElevenLabs: Connected successfully';
        testResultType = 'success';
        setElevenLabsTestStatus({ message: successMessage, type: 'success' });
        setSaveStatus(successMessage);
      } else {
        let apiError = '';
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorData = JSON.parse(errorText);
              apiError = errorData.detail?.message || errorData.message || errorText;
            } catch (parseError) {
              apiError = errorText;
            }
          }
        } catch (readError) {
          apiError = '';
        }
        
        console.warn('ElevenLabs test failed', {
          status: response.status,
          statusText: response.statusText,
          keyLength: apiKey.length
        });
        
        let errorMessage = '❌ ElevenLabs: Invalid API key';
        if (response.status === 401) {
          errorMessage = `❌ ElevenLabs: Invalid API key${apiError ? ` - ${apiError}` : ''}`;
        } else if (response.status === 403) {
          errorMessage = `❌ ElevenLabs: Access forbidden${apiError ? ` - ${apiError}` : ''}`;
        } else {
          errorMessage = `❌ ElevenLabs: Connection failed (${response.status})${apiError ? ` - ${apiError}` : ''}`;
        }
        testResultType = 'error';
        setElevenLabsTestStatus({ message: errorMessage, type: 'error' });
        setSaveStatus(errorMessage);
      }
    } catch (error) {
      const errorMessage = '❌ ElevenLabs: Not Connected - Network error';
      testResultType = 'error';
      setElevenLabsTestStatus({ message: errorMessage, type: 'error' });
      setSaveStatus(errorMessage);
      console.error('API test error:', error);
    }
    
    const autoCloseDelay = testResultType === 'success' ? 3000 : 5000;
    setTimeout(() => {
      setShowElevenLabsTestModal(false);
      setTimeout(() => setElevenLabsTestStatus(null), 300);
    }, autoCloseDelay);
    
    setTimeout(() => setSaveStatus(''), 5000);
  };

  // Test OpenRouter API key
  const testOpenRouterKey = async () => {
    if (!settings.openRouterApiKey) {
      alert('Please enter your OpenRouter API key first.');
      return;
    }
    
    // Set loading state
    setOpenRouterTestStatus({ message: 'Testing OpenRouter connection...', type: 'loading' });
    setShowTestModal(true);
    setSaveStatus(''); // Clear bottom status
    
    let testResultType = 'error'; // Default to error
    
    try {
      const isValid = await validateApiKey(settings.openRouterApiKey);
      if (isValid) {
        const successMessage = '✅ OpenRouter: Connected successfully';
        testResultType = 'success';
        setOpenRouterTestStatus({ message: successMessage, type: 'success' });
        setSaveStatus(successMessage);
        // Save the valid key
        localStorage.setItem('openrouter_api_key', settings.openRouterApiKey);
        // Reload models after saving API key
        await loadDefaultModel();
      } else {
        const errorMessage = '❌ OpenRouter: Invalid API key';
        testResultType = 'error';
        setOpenRouterTestStatus({ message: errorMessage, type: 'error' });
        setSaveStatus(errorMessage);
      }
    } catch (error) {
      const errorMessage = '❌ OpenRouter: Connection failed - ' + error.message;
      testResultType = 'error';
      setOpenRouterTestStatus({ message: errorMessage, type: 'error' });
      setSaveStatus(errorMessage);
      console.error('OpenRouter test error:', error);
    }
    
    // Auto-close modal after 3 seconds for success, 5 seconds for error
    const autoCloseDelay = testResultType === 'success' ? 3000 : 5000;
    setTimeout(() => {
      setShowTestModal(false);
      setTimeout(() => setOpenRouterTestStatus(null), 300); // Clear status after modal closes
    }, autoCloseDelay);
    
    // Also clear bottom status
    setTimeout(() => setSaveStatus(''), 5000);
  };

  const handleInputChange = (key, value) => {
    const normalizedValue = key === 'elevenLabsApiKey' ? sanitizeApiKey(value) : value;
    setSettings(prev => ({ ...prev, [key]: normalizedValue }));
    
    // Immediately save OpenRouter API key to separate storage when changed
    // This ensures it persists even if auto-save is disabled
    if (key === 'openRouterApiKey') {
      if (value && value.trim()) {
        localStorage.setItem('openrouter_api_key', value.trim());
        console.log('✅ OpenRouter API key saved to localStorage immediately');
      } else {
        localStorage.removeItem('openrouter_api_key');
        console.log('🗑️ OpenRouter API key removed from localStorage');
      }
    }
  };

  const resetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings? This cannot be undone.')) {
      setSettings({
        elevenLabsApiKey: '',
        openRouterApiKey: '',
        defaultTtsService: 'elevenlabs',
        autoSave: true
      });
      localStorage.removeItem('ttsSettings');
      localStorage.removeItem('openrouter_api_key');
      setSaveStatus('Settings reset successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Render tabs
  const renderTabs = () => (
    <div className="settings-tabs">
      <button 
        className={`tab ${activeTab === 'general' ? 'active' : ''}`}
        onClick={() => setActiveTab('general')}
      >
        General
      </button>
      <button 
        className={`tab ${activeTab === 'openrouter' ? 'active' : ''}`}
        onClick={() => setActiveTab('openrouter')}
      >
        OpenRouter AI
      </button>
      <button 
        className={`tab ${activeTab === 'wordpress' ? 'active' : ''}`}
        onClick={() => setActiveTab('wordpress')}
      >
        WordPress Logins
      </button>
      <button 
        className={`tab ${activeTab === 'tts' ? 'active' : ''}`}
        onClick={() => setActiveTab('tts')}
      >
        Text to Speech API Keys
      </button>
    </div>
  );

  // Render General Settings
  const renderGeneralSettings = () => (
    <div className="settings-section">
      <h2>General Preferences</h2>
      <div className="preference-group">
        <label>
          <input
            type="checkbox"
            checked={settings.autoSave}
            onChange={(e) => handleInputChange('autoSave', e.target.checked)}
          />
          Auto-save settings
        </label>
      </div>
    </div>
  );

  // Render OpenRouter Settings
  const renderOpenRouterSettings = () => {
    const formatDate = (timestamp) => {
      if (!timestamp) return 'Never';
      return new Date(timestamp).toLocaleString();
    };
    
    const cacheStatus = isCacheValid() ? 'Valid' : 'Expired or Missing';
    
    return (
      <div className="settings-section">
        <h2>OpenRouter AI Configuration</h2>
        <p>Configure your OpenRouter API key to enable AI content generation directly from prompts.</p>
        <div className="service-card">
          <h3>OpenRouter API</h3>
          <p>Access multiple AI models (GPT, Claude, Llama, etc.) through a single API. Free and paid models available.</p>
          <div className="api-input-group">
            <label>API Key:</label>
            <input
              type="password"
              value={settings.openRouterApiKey}
              onChange={(e) => handleInputChange('openRouterApiKey', e.target.value)}
              placeholder="Enter your OpenRouter API key"
              className="api-input"
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-secondary test-btn"
                onClick={testOpenRouterKey}
                disabled={!settings.openRouterApiKey || openRouterTestStatus?.type === 'loading'}
              >
                {openRouterTestStatus?.type === 'loading' ? 'Testing...' : 'Test Connection'}
              </button>
              <a href={OPENROUTER_KEYS_URL} target="_blank" rel="noopener noreferrer" className="help-link">
                Get API Key
              </a>
            </div>
            {/* Inline notification near test button */}
            {openRouterTestStatus && openRouterTestStatus.type !== 'loading' && (
              <div 
                className={`api-test-notification ${openRouterTestStatus.type === 'success' ? 'api-test-success' : 'api-test-error'}`}
                style={{ marginTop: '10px' }}
              >
                {openRouterTestStatus.message}
              </div>
            )}
          </div>
          <div className="info-box">
            <h4>Benefits:</h4>
            <ul>
              <li>✅ Generate content directly from prompts</li>
              <li>✅ Choose from multiple AI models</li>
              <li>✅ Free models available (GPT-3.5, Llama, Mistral, etc.)</li>
              <li>✅ No more copy-pasting between apps</li>
              <li>✅ Content appears instantly in Content Editor</li>
            </ul>
          </div>
        </div>
        
        <div className="service-card" style={{ marginTop: '20px' }}>
          <h3>Default AI Model</h3>
          <p>Choose your default AI model for content generation. This will be pre-selected in Work Plans and Prompt Forms.</p>
          
          <div className="api-input-group" style={{ marginBottom: '15px' }}>
            <label>Default Model:</label>
            {availableModelsForDefault.length > 0 ? (
              <SearchableSelect
                id="default-ai-model"
                value={defaultModel}
                onChange={setDefaultModel}
                options={availableModelsForDefault}
                placeholder="Select default AI model..."
                className="model-dropdown"
              />
            ) : (
              <p style={{ color: '#666', fontSize: '14px' }}>Loading models... Please refresh models first.</p>
            )}
            <button 
              className="btn btn-secondary test-btn"
              onClick={handleSaveDefaultModel}
            >
              Save Default Model
            </button>
          </div>
          
          <div className="info-box">
            <h4>💡 Note:</h4>
            <ul>
              <li>You can still change the model for individual tasks</li>
              <li>Free models are marked with "(free)"</li>
              <li>Leave empty to use system default</li>
            </ul>
          </div>
        </div>
        
        <div className="service-card" style={{ marginTop: '20px' }}>
          <h3>AI Models Management</h3>
          <p>Keep your model list up-to-date with the latest models from OpenRouter.</p>
          
          <div className="models-info" style={{ marginBottom: '15px', padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span><strong>Last Updated:</strong> {formatDate(modelsLastUpdated)}</span>
              <span style={{ color: isCacheValid() ? '#28a745' : '#dc3545' }}>
                <strong>Cache Status:</strong> {cacheStatus}
              </span>
            </div>
            {modelsStats.total > 0 && (
              <div style={{ display: 'flex', gap: '20px', fontSize: '14px' }}>
                <span><strong>Total Models:</strong> {modelsStats.total}</span>
                <span style={{ color: '#28a745' }}><strong>Available:</strong> {modelsStats.available}</span>
                {modelsStats.unavailable > 0 && (
                  <span style={{ color: '#dc3545' }}><strong>Unavailable:</strong> {modelsStats.unavailable}</span>
                )}
              </div>
            )}
          </div>
          
          <button 
            className="btn btn-primary"
            onClick={handleRefreshModels}
            disabled={!settings.openRouterApiKey || isRefreshingModels}
            style={{ width: '100%', marginTop: '10px' }}
          >
            {isRefreshingModels ? '🔄 Refreshing Models...' : '🔄 Refresh Models from OpenRouter'}
          </button>
          
          <div className="info-box" style={{ marginTop: '15px' }}>
            <h4>About Model Updates:</h4>
            <ul>
              <li>Models are automatically refreshed on app startup if cache is expired</li>
              <li>Cache expires after 24 hours</li>
              <li>New models are automatically added to your list</li>
              <li>Unavailable models are marked but not removed</li>
              <li>Manual refresh updates the cache immediately</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Render WordPress Settings
  const renderWordPressSettings = () => (
    <div className="settings-section">
      <h2>WordPress Site Logins</h2>
      <p>Add your WordPress sites to enable direct posting from the Content Editor.</p>
      
      {/* Add new WordPress login form */}
      <div className="wordpress-add-form">
        <h3>Add New WordPress Site</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Label (Site Name):</label>
            <input
              type="text"
              value={newWordPressLogin.label}
              onChange={(e) => setNewWordPressLogin(prev => ({ ...prev, label: e.target.value }))}
              placeholder="e.g., My Blog"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>WordPress URL:</label>
            <input
              type="url"
              value={newWordPressLogin.url}
              onChange={(e) => setNewWordPressLogin(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://yoursite.com"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Username:</label>
            <input
              type="text"
              value={newWordPressLogin.username}
              onChange={(e) => setNewWordPressLogin(prev => ({ ...prev, username: e.target.value }))}
              placeholder="your_username"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>App Password:</label>
            <input
              type="password"
              value={newWordPressLogin.appPassword}
              onChange={(e) => setNewWordPressLogin(prev => ({ ...prev, appPassword: e.target.value }))}
              placeholder="your_app_password"
              className="form-input"
            />
          </div>
        </div>
        <button 
          className="btn btn-primary"
          onClick={addWordPressLogin}
        >
          Add WordPress Site
        </button>
      </div>

      {/* WordPress logins list */}
      <div className="wordpress-logins-list">
        <h3>Saved WordPress Sites</h3>
        {wordPressLogins.length === 0 ? (
          <p className="no-logins">No WordPress sites added yet.</p>
        ) : (
          <div className="logins-table">
            <table>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>WordPress URL</th>
                  <th>Username</th>
                  <th>App Password</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {wordPressLogins.map((login) => (
                  <tr key={login.id}>
                    <td>{login.label}</td>
                    <td>{login.url}</td>
                    <td>{login.username}</td>
                    <td>{'*'.repeat(login.appPassword.length)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => testWordPressConnection(login)}
                      >
                        Test
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteWordPressLogin(login.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // Render TTS Settings
  const renderTTSSettings = () => (
        <div className="settings-section">
          <h2>Text-to-Speech Services</h2>
          <div className="service-card">
            <h3>ElevenLabs</h3>
            <p>Ultra-realistic AI voices with emotion control and voice cloning capabilities.</p>
            <p style={{ marginTop: '6px', fontSize: '0.9rem', color: '#6c757d' }}>
              Note: Paid ElevenLabs plans unlock more voices and models.
            </p>
            <div className="api-input-group">
              <label>API Key:</label>
              <input
                type="password"
                value={settings.elevenLabsApiKey}
                onChange={(e) => handleInputChange('elevenLabsApiKey', e.target.value)}
                placeholder="Enter your ElevenLabs API key"
                className="api-input"
              />
              <button 
                className="btn btn-secondary test-btn"
                onClick={testElevenLabsKey}
                disabled={!settings.elevenLabsApiKey || elevenLabsTestStatus?.type === 'loading'}
              >
                {elevenLabsTestStatus?.type === 'loading' ? 'Testing...' : 'Test Key'}
              </button>
              <a href={ELEVENLABS_HELP_URL} target="_blank" rel="noopener noreferrer" className="help-link">
                Get API Key Help
              </a>
            </div>
          </div>
          <div className="preference-group">
            <label>Default TTS Service:</label>
            <select
              value={settings.defaultTtsService}
              onChange={(e) => handleInputChange('defaultTtsService', e.target.value)}
              className="preference-select"
            >
              <option value="elevenlabs">ElevenLabs</option>
            </select>
          </div>
          </div>
  );

  return (
    <div className="settings">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Configure your application settings and API keys</p>
          </div>
      
      {renderTabs()}
      
      <div className="settings-container">
        {activeTab === 'general' && renderGeneralSettings()}
        {activeTab === 'openrouter' && renderOpenRouterSettings()}
        {activeTab === 'wordpress' && renderWordPressSettings()}
        {activeTab === 'tts' && renderTTSSettings()}
        
        <div className="settings-actions">
          <button 
            className="btn btn-primary save-btn"
            onClick={saveSettings}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
          <button 
            className="btn btn-outline reset-btn"
            onClick={resetSettings}
          >
            Reset Settings
          </button>
        </div>
        {saveStatus && (
          <div className="save-status">
            {saveStatus}
          </div>
        )}
      </div>
      
      {/* Test Result Modal */}
      {showTestModal && openRouterTestStatus && (
        <div className="test-modal-overlay" onClick={() => setShowTestModal(false)}>
          <div className="test-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="test-modal-close" 
              onClick={() => setShowTestModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div className={`test-modal-icon ${openRouterTestStatus.type}`}>
              {openRouterTestStatus.type === 'loading' && '⏳'}
              {openRouterTestStatus.type === 'success' && '✅'}
              {openRouterTestStatus.type === 'error' && '❌'}
            </div>
            <h3 className="test-modal-title">
              {openRouterTestStatus.type === 'loading' && 'Testing Connection...'}
              {openRouterTestStatus.type === 'success' && 'Connection Successful!'}
              {openRouterTestStatus.type === 'error' && 'Connection Failed'}
            </h3>
            <p className="test-modal-message">{openRouterTestStatus.message}</p>
            <button 
              className="btn btn-primary test-modal-button"
              onClick={() => setShowTestModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      {/* Settings saved / error modal */}
      {showSettingsSavedModal && (
        <div className="test-modal-overlay" onClick={() => setShowSettingsSavedModal(false)}>
          <div className="test-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="test-modal-close" 
              onClick={() => setShowSettingsSavedModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div className={`test-modal-icon ${settingsSavedError ? 'error' : 'success'}`}>
              {settingsSavedError ? '❌' : '✅'}
            </div>
            <h3 className="test-modal-title">
              {settingsSavedError ? 'Settings not saved' : 'Settings saved'}
            </h3>
            <p className="test-modal-message">
              {settingsSavedError || 'Your settings have been saved successfully.'}
            </p>
            <button 
              className="btn btn-primary test-modal-button"
              onClick={() => setShowSettingsSavedModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* ElevenLabs Test Result Modal */}
      {showElevenLabsTestModal && elevenLabsTestStatus && (
        <div className="test-modal-overlay" onClick={() => setShowElevenLabsTestModal(false)}>
          <div className="test-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="test-modal-close" 
              onClick={() => setShowElevenLabsTestModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div className={`test-modal-icon ${elevenLabsTestStatus.type}`}>
              {elevenLabsTestStatus.type === 'loading' && '⏳'}
              {elevenLabsTestStatus.type === 'success' && '✅'}
              {elevenLabsTestStatus.type === 'error' && '❌'}
            </div>
            <h3 className="test-modal-title">
              {elevenLabsTestStatus.type === 'loading' && 'Testing Connection...'}
              {elevenLabsTestStatus.type === 'success' && 'Connection Successful!'}
              {elevenLabsTestStatus.type === 'error' && 'Connection Failed'}
            </h3>
            <p className="test-modal-message">{elevenLabsTestStatus.message}</p>
            <button 
              className="btn btn-primary test-modal-button"
              onClick={() => setShowElevenLabsTestModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 